import * as Yup from 'yup'

import { FINANCING_STATUS, PERSONA_PROCESS_STATUS } from '../../../constants'
import { isManagerOrAdmin } from 'util/unified'
import { isValidURL } from '../../../util/util'
import { isSaveCardRequestEnabled as practiceSaveCardRequestEnabled } from '../../../util/practice'
import { isSaveCardRequestEnabled as consolidatorSaveCardRequestEnabled } from '../../../util/consolidator'

export const canViewStripeRequirementsBanner = (currentPractice: IPractice, currentUser: User) => {
	// Don't show stripe requirements banner when it is not loaded
	if (!currentPractice?.id || !currentUser) {
		return false
	}

	// Only practices without a consolidator (independent practice) have permission
	if (currentPractice?.consolidatorId) {
		return false
	}

	return true
}

// Only Hospital Managers or Scratch Admins have permission
export const hasPermissionForUpdatingStripeRequirements = (context, currentPractice) => {
	return isManagerOrAdmin(context, currentPractice)
}

const hasPendingImmediateRequirements = (currentPracticeObject: IPractice) =>
	currentPracticeObject.additionalStripeRequiredInfo?.length ?? false
const hasPendingFutureRequirements = (currentPracticeObject: IPractice) =>
	currentPracticeObject.additionalStripeFutureRequirements?.currently_due?.length ||
	currentPracticeObject.additionalStripeFutureRequirements?.eventually_due?.length ||
	false

export const shouldShowPracticeRequirements = (
	currentPracticeObject: IPractice,
	currentUser: User,
	isHomePage: boolean
) => {
	// Dont show practice requirements banner when practice is not active or when practice has financing enabled and is on home page
	if (
		!currentPracticeObject?.isActive ||
		(currentPracticeObject?.financingStatus === FINANCING_STATUS.ACTIVE && isHomePage)
	) {
		return false
	}

	// Dont show practice requirements banner when user hasn't permission to view
	if (!canViewStripeRequirementsBanner(currentPracticeObject, currentUser)) {
		return false
	}

	return hasPendingImmediateRequirements(currentPracticeObject)
}

export const shouldShowPracticeFutureRequirements = (
	currentPracticeObject: IPractice,
	currentUser: User
) => {
	// Dont show future requirements banner when user hasn't permission to view
	if (!canViewStripeRequirementsBanner(currentPracticeObject, currentUser)) {
		return false
	}

	// Dont show future requirements banner when immediate requirements exists
	if (hasPendingImmediateRequirements(currentPracticeObject)) {
		return false
	}

	return hasPendingFutureRequirements(currentPracticeObject)
}

export const shouldShow1099InfoBanner = (currentPracticeObject?: IPractice) => {
	const process = currentPracticeObject?.consolidatedPractice?.status?.['1099_INFO']

	// If 1099_INFO doesn't exist, nothing to show
	if (!process) return false

	// Validate hideAt
	if (process?.hideAt) {
		const hideAtDate = new Date(process.hideAt)
		if (!isNaN(hideAtDate.getTime()) && Date.now() >= hideAtDate.getTime()) {
			// hideAt is valid and in the past or now → don't show it
			return false
		}
	}

	return (
		process?.required === true &&
		![PERSONA_PROCESS_STATUS.COMPLETED, PERSONA_PROCESS_STATUS.APPROVED].includes(
			process?.status ?? ''
		)
	)
}

export const paymentRequestsEnabled = (currentPracticeObject, currentUserConsolidator) => {
	const practiceEnabled = currentPracticeObject?.settings?.isPaymentRequestEnabled
	const consolidatorEnabled = currentUserConsolidator?.featureFlags?.isPaymentRequestEnabled
	if (typeof practiceEnabled !== 'undefined') {
		return practiceEnabled
	}
	if (typeof consolidatorEnabled !== 'undefined') {
		return consolidatorEnabled
	}
	return true
}

/**
 * Card requests (the Collect Card Details tab) are switchable separately from payment
 * requests: 539 practices already have payment requests off but still need a card on file.
 * Practice setting wins, consolidator is the fallback, and the feature is off by default.
 */
export const cardRequestsEnabled = (
	currentPracticeObject?: IPractice,
	currentUserConsolidator?: IConsolidator
): boolean => {
	const practiceEnabled = practiceSaveCardRequestEnabled(currentPracticeObject)
	if (typeof practiceEnabled !== 'undefined') {
		return practiceEnabled
	}
	return consolidatorSaveCardRequestEnabled(currentUserConsolidator)
}

export const PRODUCT_OPTIONS: Array<{
	value: 'payments' | 'lending'
	label: string
	description: string
}> = [
	{
		value: 'payments',
		label: 'Payments',
		description: 'Simplify billing and save money with Scratch Checkout.'
	},
	{
		value: 'lending',
		label: 'Financing',
		description: 'Offer flexible financing with Scratch Pay.'
	}
]

export const validatePracticeWebsite = (message: string = 'Enter correct url!') => {
	return Yup.string()
		.test('url', message, function (value) {
			return isValidURL(value)
		})
		.required('Please enter website')
}
